/**
 * Phase 4 — Raise a Safety Observation. Responsive: used both on desktop
 * (/safety/raise-observation) and mobile (/m/safety).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Send, Loader2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import OfflineReadyBadge from '../../components/OfflineReadyBadge';
import { apiPost } from '../../lib/api';
import { enqueue, cachedGet, remainingPhotoSlots } from '../../lib/offlineQueue';
import { toast } from 'sonner';

const CATEGORIES = ['Unsafe Act', 'Unsafe Condition', 'Positive Observation'];
const SEVERITIES = [
  { v: 'low', label: 'Low', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  { v: 'medium', label: 'Medium', tone: 'bg-amber-50 text-amber-700 border-amber-300' },
  { v: 'high', label: 'High', tone: 'bg-orange-50 text-orange-700 border-orange-300' },
  { v: 'critical', label: 'Critical', tone: 'bg-red-50 text-red-700 border-red-300' },
];

export default function RaiseObservation() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: '', category: 'Unsafe Condition', severity: 'medium',
    description: '', location_on_site: '', immediate_action_taken: '', recommendation: '',
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.location.pathname.startsWith('/m');

  useEffect(() => {
    cachedGet('projects', '/projects').then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setProjects(list);
      if (list.length) setForm((f) => ({ ...f, project_id: f.project_id || list[0].id }));
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSaving(true);

    const queueOffline = async () => {
      const remaining = await remainingPhotoSlots();
      if (photos.length > remaining) {
        toast.error(`Only ${remaining} offline photo slot(s) left — sync pending entries first.`);
        return false;
      }
      await enqueue('safety_observation', { payload: { ...form }, photos });
      toast.success('Saved offline — will sync when you reconnect');
      navigate('/m/safety');
      return true;
    };

    try {
      if (isMobile && typeof navigator !== 'undefined' && !navigator.onLine) {
        await queueOffline();
        setSaving(false);
        return;
      }
      await apiPost('/safety/observations', { ...form, photos_base64: photos });
      toast.success('Safety observation submitted');
      navigate('/app/safety');
    } catch (e) {
      if (isMobile && !e?.response && await queueOffline()) {
        setSaving(false);
        return;
      }
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Safety" title="Raise Observation" subtitle="Report an unsafe act, unsafe condition, or a positive observation" />

      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="raise-observation-form">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="obs-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Category</label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, category: c })}
                className={`px-2 py-2.5 rounded-sm text-xs font-medium border transition-colors ${form.category === c ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
                data-testid={`obs-category-${c.replace(/\s/g, '-').toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Severity</label>
          <div className="grid grid-cols-4 gap-2">
            {SEVERITIES.map((s) => (
              <button key={s.v} type="button" onClick={() => setForm({ ...form, severity: s.v })}
                className={`px-2 py-2.5 rounded-sm text-xs font-bold border transition-all ${form.severity === s.v ? `${s.tone} ring-2 ring-offset-1 ring-current` : 'bg-white text-slate-500 border-slate-300'}`}
                data-testid={`obs-severity-${s.v}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">What did you observe?</label>
          <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Describe the unsafe act/condition or positive behaviour" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="obs-description" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Location on site</label>
          <input value={form.location_on_site} onChange={set('location_on_site')} placeholder="e.g. Block B, 3rd floor slab" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="obs-location" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Photos</label>
          <PhotoCaptureField photos={photos} setPhotos={setPhotos} />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Immediate action taken (optional)</label>
          <textarea value={form.immediate_action_taken} onChange={set('immediate_action_taken')} rows={2} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="obs-immediate-action" />
        </div>

        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Recommendation (optional)</label>
          <textarea value={form.recommendation} onChange={set('recommendation')} rows={2} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="obs-recommendation" />
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium"
          data-testid="obs-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
          {saving ? 'Submitting…' : 'Submit Observation'}
        </button>
        {isMobile && <div className="flex justify-center pt-1"><OfflineReadyBadge /></div>}
      </div>
    </div>
  );
}
